import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {
  NO_CALENDER_VIEW_PUBLICATION,
  PublicationShortname,
} from "@src/app/auth/serviceIdentifier";
import cx from "classnames";
import { ReactElement, useState } from "react";
import pastIssueImage from "src/app/assets/archive.json";
import downloadImage from "src/app/assets/download.svg";
import phoneImage from "src/app/assets/epaper.json";
import articleViewImage from "src/app/assets/html.json";
import quickAccessImage from "src/app/assets/menu.json";
import shareImage from "src/app/assets/share.svg";
import tileImage from "src/app/assets/tile.json";
import toggleImage from "src/app/assets/toggle.svg";
import { TGtmData } from "src/app/pages/Home";
import { EP_GA_EVENT_NAME, PAGE_NAME } from "src/trackers/googleAnalytics";

import Lottie from "../Lottie";
import { Modal } from "../Modal";
import Slider from "../Slider";
import CarouselButton, { CarouselButtonTheme } from "./CarouselButton";
import styles from "./index.module.scss";
import { OnboardingSlide } from "./OnboardingSlide";

const onBoardingImages = [
  { image: phoneImage, type: "animation" },
  { image: articleViewImage, type: "animation" },
  { image: pastIssueImage, type: "animation" },
  { image: quickAccessImage, type: "animation" },
  { image: tileImage, type: "animation" },
  { image: downloadImage, type: "static" },
  { image: shareImage, type: "static" },
  { image: toggleImage, type: "static" },
];

type Props = {
  onClose: (pagination: number) => void;
  handleGAEvent: (eventName: EP_GA_EVENT_NAME, gtmData: TGtmData) => void;
  publicationId: PublicationShortname | null;
};

export function OnboardingModal({
  onClose,
  handleGAEvent,
  publicationId,
}: Props): ReactElement {
  const [currentSlide, setCurrentSlide] = useState(0);

  const showAllSlides = !(
    publicationId && NO_CALENDER_VIEW_PUBLICATION.includes(publicationId)
  );

  const hideArticleAndCalenderSlide =
    publicationId === PublicationShortname.BM ||
    publicationId === PublicationShortname.MM;

  const getNumberOfSlides = () => {
    if (showAllSlides) {
      // all slides visible for publications are not ZB/SM or weekly publications
      return onBoardingImages.length;
    } else if (hideArticleAndCalenderSlide) {
      //show 3 slides (no calendar & article html slides) when publication is BM/MM
      return onBoardingImages.length - 2;
    } else {
      //show 4 slides (no calendar slides) when publications weekly publications & ZB/SM
      return onBoardingImages.length - 1;
    }
  };

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (_current: number, next: number) => {
      // to call page view GA event on forward button click
      if (currentSlide < next) {
        handleGAEvent(EP_GA_EVENT_NAME.PAGE_VIEW, {
          chapter1: PAGE_NAME.ONBOARDING,
          pageName: PAGE_NAME.ONBOARDING,
          pagination: next + 1,
        });
      }

      setCurrentSlide(next);
    },
    nextArrow:
      currentSlide >= getNumberOfSlides() - 1 ? (
        <CarouselButton
          theme={CarouselButtonTheme.CLOSE}
          alt="done tick image"
          onClose={() => {
            onClose(getNumberOfSlides() + 1);
          }}
        />
      ) : (
        <CarouselButton
          theme={CarouselButtonTheme.NEXT}
          alt="next arrow image"
        />
      ),
    prevArrow: (
      <CarouselButton
        theme={CarouselButtonTheme.BACK}
        alt="back arrow image"
        isDisabled={currentSlide === 0}
      />
    ),
  };

  const getSlide = (index: number, data: any) => {
    return (
      <OnboardingSlide slideNumber={index} key={index}>
        <div
          className={cx({
            [styles.onBoardingImages]: true,
            [styles.imgShadow]: currentSlide !== 0,
          })}
        >
          {data.type === "static" ? (
            <img
              src={data.image}
              alt={data.alt}
              className={styles.staticImage}
            />
          ) : (
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData: data.image,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
            />
          )}
        </div>
      </OnboardingSlide>
    );
  };

  return (
    <Modal
      onClose={() => {
        onClose(currentSlide + 1);
      }}
    >
      <div className={cx(styles.onBoardingModalBody)}>
        <Slider {...settings}>
          {onBoardingImages.map((data, index: number) => {
            //show all the slides when pubs are not ZB/SM and weekly pubs
            if (showAllSlides) {
              return getSlide(index, data);
            }

            if (hideArticleAndCalenderSlide) {
              //hide calender and article slide (i.e.2nd and 3rd slide as index is starting from 0) when pubs are ZB/SM
              if (index != 1 && index != 2) {
                return getSlide(index, data);
              }
            } else {
              //hide calender slide (i.e. 3rd slide as index is starting from 0) when pubs are ZB/SM and weekly pubs
              if (index != 2) {
                return getSlide(index, data);
              }
            }
          })}
        </Slider>
      </div>
    </Modal>
  );
}
